/**
 * The persistent connection switcher in the navbar (FR-012).
 *
 * The active connection is on show wherever the user is, and changing it is a
 * settings write and nothing else: a server whose backend session still works
 * is switched to without anybody signing in again (scenario 10). The one
 * exception is a connection whose authorisation can no longer be renewed;
 * choosing that one starts a re-authorisation instead, asking to be returned to
 * whatever the user was doing (scenario 11, FR-058).
 *
 * Connections that have never completed an authorisation are not offered here,
 * because they cannot be worked with yet; the connections screen is where they
 * get connected.
 *
 * @author John Grimes
 */

import {
  ChevronDownIcon,
  PlugIcon,
  ServerIcon,
  SignInIcon,
} from "@primer/octicons-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router";

import { PlainError } from "./PlainError";
import { plainErrorOf } from "../features/auth/useSession";
import {
  hostOf,
  useActivateConnection,
  useAuthoriseConnection,
  useConnections,
  useSettings,
} from "../features/connections/useConnections";

import type { JSX } from "react";
import type { PlainError as PlainErrorBody } from "shared/api";
import type { ConnectionSummary } from "shared/connection";

/**
 * Renders the switcher.
 *
 * @returns The switcher element.
 *
 * @example
 * ```tsx
 * <ConnectionSwitcher />
 * ```
 */
export const ConnectionSwitcher = (): JSX.Element => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const [failure, setFailure] = useState<PlainErrorBody>();

  const { connections } = useConnections();
  const { settings } = useSettings();
  const activate = useActivateConnection();
  const authorise = useAuthoriseConnection();

  const active = connections.find(
    (connection) => connection.id === settings?.activeConnectionId,
  );
  // A connection that has never been authorised cannot be worked with.
  const ready = connections.filter(
    (connection) => connection.status !== "pending",
  );
  const switching = activate.isPending || authorise.isPending;

  // A press anywhere outside the switcher dismisses the panel. Presses inside
  // it, the toggle included, are left to their own handlers.
  useEffect(() => {
    if (!open) return;
    const dismiss = (event: PointerEvent): void => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", dismiss);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
    };
  }, [open]);

  /**
   * Acts on the connection the user picked: a re-authorisation when its
   * authorisation has lapsed, otherwise a settings write.
   *
   * @param connection - The connection the user chose.
   */
  const choose = (connection: ConnectionSummary): void => {
    setFailure(undefined);
    if (connection.status === "needs_reauth") {
      authorise.mutate(
        { connection, returnPath: pathname },
        {
          onSuccess: ({ redirectUrl }) => {
            // The server's own sign-in page, reached by leaving the app
            // (FR-008, FR-010).
            location.assign(redirectUrl);
          },
          onError: (error: unknown) => {
            setFailure(plainErrorOf(error));
          },
        },
      );
      return;
    }
    activate.mutate(connection, {
      onSuccess: () => {
        setOpen(false);
      },
      onError: (error: unknown) => {
        setFailure(plainErrorOf(error));
      },
    });
  };

  return (
    <div ref={container} className="relative">
      <button
        type="button"
        className="btn btn-ghost max-w-72 gap-1.5 font-normal"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          setOpen(!open);
        }}
      >
        <ServerIcon size={14} aria-hidden />
        <span className="truncate">
          {active === undefined ? (
            "No server chosen"
          ) : (
            <>
              <span className="sr-only">Server: </span>
              {active.name}
            </>
          )}
        </span>
        <ChevronDownIcon size={12} aria-hidden />
      </button>
      {open && (
        // The panel hangs off the button rather than off a fixed offset from
        // the top of the page: below `sm` the switcher sits on a row of its
        // own, so a hard-coded navbar height would put the panel over it.
        <div className="absolute top-full left-0 right-0 mt-1 sm:right-auto sm:w-72 z-50 card bg-base-100 border border-base-300 shadow-lg">
          <div className="card-body p-2 gap-2">
            {failure && <PlainError error={failure} />}
            {ready.length === 0 ? (
              <p className="text-sm opacity-60 px-2 m-0">
                No servers are ready to work with yet.
              </p>
            ) : (
              <div role="menu" className="flex flex-col gap-0.5">
                {ready.map((connection) => (
                  <button
                    key={connection.id}
                    type="button"
                    role="menuitem"
                    className="btn btn-ghost justify-start h-auto py-1.5 font-normal"
                    aria-current={
                      connection.id === active?.id ? "true" : undefined
                    }
                    disabled={switching}
                    title={switching ? "Changing server now…" : undefined}
                    onClick={() => {
                      choose(connection);
                    }}
                  >
                    <span className="flex-1 min-w-0 flex flex-col items-start">
                      <span className="text-sm truncate max-w-full">
                        {connection.name}
                      </span>
                      <span className="text-sm row-hint font-mono truncate max-w-full">
                        {hostOf(connection.baseUrl)}
                      </span>
                    </span>
                    {connection.status === "needs_reauth" && (
                      <span className="badge badge-warning badge-soft gap-1">
                        <SignInIcon size={12} aria-hidden />
                        Needs signing in again
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
            <Link
              to="/connections"
              className="btn btn-ghost justify-start gap-1.5 font-normal"
            >
              <PlugIcon size={14} aria-hidden />
              Manage connections
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};
